var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useContext, useEffect, useImperativeHandle, useState, } from "@wordpress/element";
import { __ } from "@wordpress/i18n";
import { ModalContext } from "../../../providers/ModalContextProvider";
import { WPQTModalField, WPQTModalFieldSet, WPQTModalFooter, } from "../WPQTModal";
import { ArchiveBoxIcon, ArrowUturnUpIcon, CheckBadgeIcon, TrashIcon, } from "@heroicons/react/24/outline";
import { ADD_ASSIGNED_USER_TO_EDITING_TASK, CLOSE_TASK_MODAL, PIPELINE_CHANGE_TASK_DONE_STATUS, REMOVE_ARCHIVED_TASK, REMOVE_ASSIGNED_USER_FROM_EDITING_TASK, } from "../../../constants";
import { useTaskActions } from "../../../hooks/actions/useTaskActions";
import { useLoadingStates } from "../../../hooks/useLoadingStates";
import { ActivePipelineContext } from "../../../providers/ActivePipelineContextProvider";
import { AppContext } from "../../../providers/AppContextProvider";
import { ArchiveContext } from "../../../providers/ArchiveContextProvider";
import { CustomFieldEntityType } from "../../../types/custom-field";
import { WPQTIconButton } from "../../common/Button/Button";
import { WPQTInput } from "../../common/Input/Input";
import { WPQTTextarea } from "../../common/TextArea/TextArea";
import { Toggle } from "../../common/Toggle/Toggle";
import { CustomFieldsInModalWrap } from "../../CustomField/CustomFieldsInModalWrap/CustomFieldsInModalWrap";
import { UserAssignementDropdown } from "../../Dropdown/UserAssignementDropdown/UserAssignementDropdown";
import { LoadingOval } from "../../Loading/Loading";
import { TaskModalTabs } from "../../Tab/CommentsAndLogs/TaskModalTabs/TaskModalTabs";
const TaskModalContent = forwardRef(({ taskModalSaving, editTask, deleteTask }, ref) => {
    const { state: { taskToEdit }, modalDispatch, } = useContext(ModalContext);
    const { state: { isUserAllowedToDelete }, } = useContext(AppContext);
    const { state: { activePipeline }, fetchAndSetPipelineData, } = useContext(ActivePipelineContext);
    const { archiveDispatch } = useContext(ArchiveContext);
    const [taskName, setTaskName] = useState("");
    const [taskDescription, setTaskDescription] = useState("");
    const [freeForAllTask, setFreeForAllTask] = useState(false);
    const { archiveTask, restoreArchivedTask } = useTaskActions();
    const { loading1: isDeletingTask, setLoading1: setIsDeletingTask, loading2: archiveLoading, setLoading2: setArchiveLoading, } = useLoadingStates();
    const isTaskArchived = taskToEdit === null || taskToEdit === void 0 ? void 0 : taskToEdit.is_archived;
    const pipelineExists = (taskToEdit === null || taskToEdit === void 0 ? void 0 : taskToEdit.pipeline_name) !== null;
    useEffect(() => {
        if (taskToEdit) {
            setTaskName(taskToEdit.name);
            setTaskDescription(taskToEdit.description);
            setFreeForAllTask(taskToEdit.free_for_all);
        }
    }, [taskToEdit]);
    const saveTask = () => {
        if (taskToEdit) {
            editTask(Object.assign(Object.assign({}, taskToEdit), { name: taskName, description: taskDescription, free_for_all: freeForAllTask }));
        }
    };
    const clearContent = () => {
        setTaskName("");
        setTaskDescription("");
    };
    useImperativeHandle(ref, () => ({
        clearContent,
    }));
    if (!taskToEdit) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "wpqt-grid wpqt-grid-cols-1 wpqt-gap-7 md:wpqt-grid-cols-[1fr_auto]", children: [_jsxs("div", { className: "wpqt-border-0 wpqt-border-r wpqt-border-solid wpqt-border-r-gray-300 md:wpqt-pr-3", children: [_jsxs("div", { className: "wpqt-mb-5 wpqt-grid wpqt-grid-cols-1 wpqt-gap-4 md:wpqt-grid-cols-[auto_1fr]", children: [_jsxs(WPQTModalFieldSet, { children: [_jsx(WPQTModalField, { label: __("Name", "quicktasker"), children: _jsx(WPQTInput, { isAutoFocus: true, value: taskName, onChange: (newValue) => setTaskName(newValue) }) }), _jsx(WPQTModalField, { label: __("Description", "quicktasker"), children: _jsx(WPQTTextarea, { rowsCount: 3, value: taskDescription, onChange: (newValue) => setTaskDescription(newValue) }) }), _jsx(WPQTModalField, { label: __("Assigned users", "quicktasker"), children: _jsx(UserAssignementDropdown, { task: taskToEdit, onUserAdd: (user) => {
                                                        modalDispatch({
                                                            type: ADD_ASSIGNED_USER_TO_EDITING_TASK,
                                                            payload: user,
                                                        });
                                                    }, onUserDelete: (user) => {
                                                        modalDispatch({
                                                            type: REMOVE_ASSIGNED_USER_FROM_EDITING_TASK,
                                                            payload: user,
                                                        });
                                                    } }) }), _jsx(WPQTModalField, { label: __("Free for all task", "quicktasker"), tooltipId: `free-for-all-${taskToEdit.id}-tooltip`, tooltipText: __("When enabled, all QuickTasker users have the ability to self-assign or unassign this task.", "quicktasker"), children: _jsx(Toggle, { checked: freeForAllTask, handleChange: setFreeForAllTask }) }), _jsx(TaskDoneStatus, { taskId: taskToEdit.id, isCompleted: taskToEdit.is_done })] }), _jsx("div", { children: _jsx(CustomFieldsInModalWrap, { entityId: taskToEdit.id, entityType: CustomFieldEntityType.Task }) })] }), _jsx("div", { className: "wpqt-mt-7 md:wpqt-pr-3", children: _jsx(TaskModalTabs, { task: taskToEdit }) })] }), _jsxs("div", { className: "wpqt-flex wpqt-flex-col wpqt-gap-2", children: [isTaskArchived && pipelineExists && (_jsx(WPQTIconButton, { icon: _jsx(ArrowUturnUpIcon, { className: "wpqt-icon-green wpqt-size-5" }), text: __("Restore task", "quicktasker"), onClick: () => {
                                    restoreArchivedTask(taskToEdit.id, () => {
                                        modalDispatch({ type: CLOSE_TASK_MODAL });
                                        archiveDispatch({
                                            type: REMOVE_ARCHIVED_TASK,
                                            payload: taskToEdit.id,
                                        });
                                    });
                                } })), !isTaskArchived && (_jsx(WPQTIconButton, { icon: _jsx(ArchiveBoxIcon, { className: "wpqt-icon-blue wpqt-size-5" }), text: __("Archive task", "quicktasker"), loading: archiveLoading, onClick: () => {
                                    setArchiveLoading(true);
                                    archiveTask(taskToEdit.id, () => {
                                        modalDispatch({ type: CLOSE_TASK_MODAL });
                                        fetchAndSetPipelineData(activePipeline.id);
                                        setArchiveLoading(false);
                                    });
                                } })), isUserAllowedToDelete && (_jsx(WPQTIconButton, { icon: _jsx(TrashIcon, { className: "wpqt-icon-red wpqt-size-5" }), text: __("Delete task", "quicktasker"), loading: isDeletingTask, onClick: () => __awaiter(void 0, void 0, void 0, function* () {
                                    setIsDeletingTask(true);
                                    yield deleteTask(taskToEdit);
                                    modalDispatch({ type: CLOSE_TASK_MODAL });
                                    setIsDeletingTask(false);
                                }) }))] })] }), _jsx(WPQTModalFooter, { onSave: saveTask, saveBtnText: taskModalSaving
                    ? __("Saving...", "quicktasker")
                    : __("Save", "quicktasker") })] }));
});
function TaskDoneStatus({ isCompleted, taskId }) {
    const { modalDispatch } = useContext(ModalContext);
    const { dispatch } = useContext(ActivePipelineContext);
    const [isLoading, setIsLoading] = useState(false);
    const { changeTaskDoneStatus } = useTaskActions();
    const toggleTaskDontStatus = () => __awaiter(this, void 0, void 0, function* () {
        setIsLoading(true);
        yield changeTaskDoneStatus(taskId, !isCompleted, (isCompleted) => {
            modalDispatch({
                type: PIPELINE_CHANGE_TASK_DONE_STATUS,
                payload: { done: isCompleted },
            });
            dispatch({
                type: PIPELINE_CHANGE_TASK_DONE_STATUS,
                payload: { taskId, done: isCompleted },
            });
        });
        setIsLoading(false);
    });
    return (_jsx(WPQTModalField, { label: isCompleted
            ? __("Task completed", "quicktasker")
            : __("Task not completed", "quicktasker"), children: isLoading ? (_jsx(LoadingOval, { width: "24", height: "24" })) : (_jsx(CheckBadgeIcon, { onClick: toggleTaskDontStatus, className: `wpqt-size-6 ${isCompleted ? "wpqt-icon-green" : "wpqt-text-gray-300"} wpqt-cursor-pointer` })) }));
}
export { TaskModalContent };
